import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';

const PrivacyPolicy = () => {
  return (
    <>
      <SEOHead
        title="Privacy Policy - Namvoro"
        description="Namvoro's privacy policy outlines how we protect and handle your personal information with enterprise-grade security measures."
        keywords="privacy policy, data protection, cybersecurity, enterprise security, GDPR compliance"
        canonicalUrl="https://namvoro.com/privacy-policy"
      />
      
      <div className="min-h-screen bg-white pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl font-bold text-slate-900 mb-8">Privacy Policy</h1>
            <p className="text-slate-600 mb-8">Last updated: December 2024</p>
            
            <div className="prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Information We Collect</h2>
                <p className="text-slate-700 mb-4">
                  Namvoro collects information you provide directly to us, such as when you create an account, request services, or communicate with us. This may include:
                </p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Contact information (name, email, phone number, address)</li>
                  <li>Business information (company name, industry, domain portfolios)</li>
                  <li>Technical information (IP addresses, browser type, device information)</li>
                  <li>Service usage data and preferences</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. How We Use Your Information</h2>
                <p className="text-slate-700 mb-4">We use the information we collect to:</p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process transactions and manage your account</li>
                  <li>Communicate with you about services, updates, and security alerts</li>
                  <li>Comply with legal obligations and protect against fraud</li>
                  <li>Analyze usage patterns to enhance user experience</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Information Sharing</h2>
                <p className="text-slate-700 mb-4">
                  We maintain strict confidentiality and do not sell, trade, or rent your personal information. We may share information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>With trusted service providers who assist in delivering our services</li>
                  <li>When required by law or to protect our rights and safety</li>
                  <li>In connection with a business transaction (merger, acquisition, etc.)</li>
                  <li>With your explicit consent</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Data Security</h2>
                <p className="text-slate-700 mb-4">
                  We implement enterprise-grade security measures to protect your information, including:
                </p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Encryption in transit and at rest</li>
                  <li>Regular security audits and penetration testing</li>
                  <li>Access controls and authentication protocols</li>
                  <li>Compliance with industry security standards</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Data Retention</h2>
                <p className="text-slate-700 mb-4">
                  We retain your information for as long as necessary to provide services and comply with legal obligations. You may request deletion of your data by contacting us at privacy@namvoro.com.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Your Rights</h2>
                <p className="text-slate-700 mb-4">You have the right to:</p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Access and review your personal information</li>
                  <li>Correct inaccurate or incomplete data</li>
                  <li>Request deletion of your data</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Data portability where applicable</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. International Transfers</h2>
                <p className="text-slate-700 mb-4">
                  Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Contact Us</h2>
                <p className="text-slate-700 mb-4">
                  If you have questions about this Privacy Policy, please contact us at:
                </p>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-slate-700">
                    <strong>Email:</strong> privacy@namvoro.com<br />
                    <strong>Phone:</strong> +1 855-425-5551<br />
                    <strong>Address:</strong> 200 Broadway, Floor 3, New York, NY 10038
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;